import { AxiosHeaders, AxiosRequestConfig, AxiosRequestHeaders } from "axios";

import { GranularCatalogConfig } from "../models";

type GranularRequestConfigOmissions = {
  omitAuthToken?: boolean;
  omitOperationId?: boolean;
  omitLocale?: boolean;
};

type GranularRequestConfigOptions = {
  // The "omissions" options are provided as a way of entirely
  // omitting the corresponding data from the request config,
  // even if the getter for that data is provided and returns a value.
  //
  // This may be necessary for endpoints that cannot
  // handle receiving unexpected headers.
  omissions?: GranularRequestConfigOmissions;
  // Allow users to add any additional axios configuration they may need.
  axiosRequestConfig?: AxiosRequestConfig;
};

export const createGranularRequestConfig = (
  catalogConfig: GranularCatalogConfig,
  options: GranularRequestConfigOptions = {},
): AxiosRequestConfig => {
  const { omissions = {}, axiosRequestConfig = {} } = options;
  const { omitAuthToken, omitOperationId, omitLocale } = omissions;

  const { getAuthToken, getOperationId, getLocale } = catalogConfig;

  const granularRequestHeaders: AxiosRequestHeaders = new AxiosHeaders();

  // Try to add the authToken header if we can and should.
  try {
    if (!omitAuthToken && getAuthToken) {
      const authToken = getAuthToken();
      granularRequestHeaders.set("Authorization", `Bearer ${authToken}`);
    }
  } catch (error) {
    // Do nothing.
    // If authToken is actually required, let the endpoint fail.
  }

  // Try to add the operationId header if we can and should.
  try {
    if (!omitOperationId && getOperationId) {
      const operationId = getOperationId();
      granularRequestHeaders.set("x-operation", operationId);
    }
  } catch (error) {
    // Do nothing.
    // If operationId is actually required, let the endpoint fail.
  }

  // Try to add the locale header if we can and should.
  try {
    if (!omitLocale && getLocale) {
      const locale = getLocale();
      granularRequestHeaders.set("X-Granular-Locale", locale);
    }
  } catch (error) {
    // Do nothing.
    // If locale is actually required, let the endpoint fail.
  }

  return {
    ...axiosRequestConfig,
    headers: {
      ...axiosRequestConfig.headers,
      common: {
        ...(axiosRequestConfig as any).headers?.common,
        ...granularRequestHeaders,
      },
    },
  };
};
